import { Link as RouterLink } from 'react-router-dom';
import { motion, useReducedMotion } from 'motion/react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { Eyebrow, Section, monoSm } from './Section';
import { HERO } from './copy';

const HEADING_ID = 'hero-title';

export function Hero() {
  const reduce = useReducedMotion();
  /** The one page-load sequence: fade + 12px rise, 450ms, staggered 120ms. */
  const step = (i: number) => ({
    initial: reduce ? false : { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.45, delay: i * 0.12 },
  });

  return (
    <Section
      labelledBy={HEADING_ID}
      divider={false}
      sx={{ pt: { xs: 5, md: 9 }, pb: { xs: 4, md: 6 } }}
    >
      <motion.div {...step(0)}>
        <Eyebrow>{HERO.eyebrow}</Eyebrow>
      </motion.div>

      <motion.div {...step(1)}>
        <Typography variant="h1" id={HEADING_ID} sx={{ maxWidth: 900 }}>
          {HERO.headline.before}
          <Box component="em" sx={{ fontStyle: 'italic' }}>
            {HERO.headline.emphasis}
          </Box>
          {HERO.headline.after}
        </Typography>
      </motion.div>

      <motion.div {...step(2)}>
        <Typography
          sx={{
            mt: { xs: 3, md: 4 },
            maxWidth: '60ch',
            fontSize: '1.125rem',
            lineHeight: 1.6,
            color: 'var(--ink-muted)',
          }}
        >
          {HERO.standfirst}
        </Typography>
      </motion.div>

      <motion.div {...step(3)}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: { xs: 4, md: 5 } }}>
          <Button
            component={RouterLink}
            to={HERO.primary.to}
            variant="contained"
            size="large"
            sx={{ minHeight: 44, alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
          >
            {HERO.primary.label}
          </Button>
          <Button
            component={RouterLink}
            to={HERO.secondary.to}
            variant="outlined"
            size="large"
            sx={{
              minHeight: 44,
              alignSelf: { xs: 'stretch', sm: 'flex-start' },
              borderColor: 'var(--outline-strong)',
              color: 'var(--ink)',
              '&:hover': {
                borderColor: 'var(--outline-strong)',
                backgroundColor: 'var(--bg-elevated)',
              },
            }}
          >
            {HERO.secondary.label}
          </Button>
        </Stack>
      </motion.div>

      <motion.div {...step(4)}>
        <Typography
          component="p"
          sx={{
            ...monoSm,
            mt: { xs: 4, md: 5 },
            pt: 2,
            borderTop: '1px solid var(--rule)',
          }}
        >
          {HERO.spec.join(' · ')}
        </Typography>
      </motion.div>
    </Section>
  );
}
